import { Injectable } from '@angular/core';
import { HTTPWrapperContext, RequestConfigImplementation, REQUEST_METHODS, XHR_STRATEGIES } from '@ptl/xhr';
import { DrugLookupService } from './drug-lookup.service';
import {
  SelectedMedication,
  MedicationStateStructure
} from '../reducers/medication.reducer';
import { DrugDosage } from 'src/app/shared/interfaces/drug-dosage.interface';
import {
  DRXDosagePayload,
  DRXDatapowerSessionStartPayload,
  DRXSessionStartResponse,
  DRXRetrieveAllProductsResponse,
  DRXRetrieveSingleProductResponse,
  DRXGraphAndTableData,
  DRXMonthlyCosts,
  DRXRetrieveAllProductsItem,
  DRXPharmacyPayload,
  DRXMemberSearchPayload,
  DRXMemberSearchResponse,
  PharmacyCost,
  DRXGraphAndTableDataChild,
  DRXCostObj,
  DRXAxwaySessionStartPayload,
} from 'src/app/shared/interfaces/drx.interface';
import {
  setSessionId,
  setDrxAllProductsResponse,
  setDrxSingleProductResponse,
  DRXStateStructure,
  setCostsData,
  setPreviousEncodedSessionMeds,
  setPreviousEncodedDetailMeds,
  setSelectedPlanContractId,
  setEffectiveDateStored,
  setCountyStored
} from '../reducers/drx-response.reducer';
import { StateStructure } from '@ptl/state';
import { NgRedux } from '@ptl/angular-redux-store';
import { ReducerUtilityService } from 'src/app/shared/utility-services/reducer-utility.service';
import { ZipCountyStateStructure } from 'src/app/angular-elements/shared/interfaces/zipCountyStateStructure.interface';
import { CurrencyPipe } from '@angular/common';
import { DrugItem } from 'src/app/shared/interfaces/drug-item.interface';
import { PlansStateStructure } from 'src/app/angular-elements/plan-selection/configs/interfaces/plansStateStructure.interface';
import { MedicareAdvantage_and_PDP_Product } from 'src/app/angular-elements/plan-selection/interfaces/product.interface';
import { BehaviorSubject } from 'rxjs';
import { EffectiveDateStateStructure } from 'src/app/angular-elements/shared/components/configs/interfaces/effective-date.state-structure.interface';
import { DateService } from 'src/app/shared/services/date.service';
import { MedicareAdvantage_and_PDP_PlanAttributes } from 'src/app/angular-elements/plan-selection/interfaces/plan-attributes.interface';
import { MAIL_PHARMACY_TYPE_ID, PharmacyState, PHARMACY_REDUX_STATE_ID, RETAIL_PHARMACY_TYPE_ID } from '../../pharmacy-information/configs/reducers/pharmacy.reducer';
import { Pharmacy } from '../../pharmacy-information/configs/interfaces/pharmacy.interface';
import { ContentStorageService } from '@ptl/global-components';
import { environment } from 'src/environments/environment';
import { brandMN, brandND, brandWY } from '@ptl/brand';
import { HttpHeaders } from '@angular/common/http';

@Injectable({
  providedIn: 'root'
})
export class DrxService {

  drugCache: {[key: string]: DRXRetrieveSingleProductResponse} = {};

  sessionStarting = false; // stop race conditions by tables/graphs

  drxSessionDataBehaviorSubject: BehaviorSubject<DRXRetrieveAllProductsItem[]> = new BehaviorSubject(null);
  drxSessionData$ = this.drxSessionDataBehaviorSubject.asObservable();

  monthsArray: string[] = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December"
  ];

  tableCategoriesContent: string[] = ["Coverage Phase", "Drug Restrictions"];


  RETAIL_COST_HEADER = 'Full Cost of Drug';
  INITIAL_COVERAGE_HEADER = 'Initial Coverage Period';
  COVERAGE_GAP_HEADER = 'Coverage Gap';
  DEDUCTIBLE_HEADER = 'Deductible';
  headerRowContent: string[] = [
    this.RETAIL_COST_HEADER,
    this.INITIAL_COVERAGE_HEADER,
    "Catastrophic",
    "Prior Auth.",
    "Quantity Limits",
    "Step Therapy"
  ];

  monthlyCostsHeaderContent: string[] = ['Drug Cost', 'Drug Phase', 'Premium', 'Total Monthly Cost'];

  constructor(
    private drugLookupService: DrugLookupService,
    private ngRedux: NgRedux<StateStructure>,
    private reducerUtilityService: ReducerUtilityService,
    private currencyPipe: CurrencyPipe,
    private httpClient: HTTPWrapperContext,
    private contentStorage: ContentStorageService,
    private dateService: DateService

  ) {

    const drxServiceContent = this.contentStorage.getContentMap('drxServiceContent') as Map<any, any>;

    if (drxServiceContent.size > 0) {
      if (drxServiceContent.get('monthsArray')) {
        this.monthsArray = JSON.parse(drxServiceContent.get('monthsArray')) as string[];
      }
      if (drxServiceContent.get('tableCategoriesContent')) {
        this.tableCategoriesContent = JSON.parse(drxServiceContent.get('tableCategoriesContent')) as string[];
      }
      if (drxServiceContent.get('headerRowContent')) {
        this.headerRowContent = JSON.parse(drxServiceContent.get('headerRowContent')) as string[];
      }
    }


    const drxServiceData: DRXStateStructure = this.reducerUtilityService.getState('DrxServiceData');

    if (drxServiceData.drxAllProductsResponse) {
      this.drxSessionDataBehaviorSubject.next(drxServiceData.drxAllProductsResponse);
    }

  }

  public hasNewMedsOrPharmacies(comparableEncodedMeds: string): boolean {
    const medState: MedicationStateStructure = this.reducerUtilityService.getState('MedicationInformation');

    return medState &&
      medState.currentEncodedMeds !== null &&
      this.getCurrentEncodedMedsAndPharmacies() !== comparableEncodedMeds;
  }

  private getCurrentEncodedMedsAndPharmacies(): string {
    const medState: MedicationStateStructure = this.reducerUtilityService.getState('MedicationInformation');
    const pharmacyState: PharmacyState = this.reducerUtilityService.getState(PHARMACY_REDUX_STATE_ID);
    
    return window.btoa(JSON.stringify(medState.medicationForm.selectedMedications) + JSON.stringify(pharmacyState.pharmacys))
  }

  public async updateSession() {
    const getTimeNowInMS = (): number => this.dateService.getTodayAsMoment().valueOf();
    const fromMillisecondsToSeconds = (timeInMS: number): number => timeInMS / 1000;
    const isSessionUnstartedOrExpired = (startTimeInMS: number): boolean => {
      /** If there was no start time, then the session does not exist, therefore is not expired */
      if (!startTimeInMS) { return true; }

      const sessionExpirationThreshold = 3600;
      return fromMillisecondsToSeconds(getTimeNowInMS() - startTimeInMS) >= sessionExpirationThreshold;
    };

    if (!this.sessionStarting) {
      this.sessionStarting = true;

      const medState: MedicationStateStructure = this.reducerUtilityService.getState('MedicationInformation');
      const drxState: DRXStateStructure = this.reducerUtilityService.getState('DrxServiceData');

      // clear drug data if no meds added
      if (medState.currentEncodedMeds === null) {
        this.ngRedux.dispatch(setDrxAllProductsResponse(null));
        this.ngRedux.dispatch(setCostsData(null, null, null));
        this.drxSessionDataBehaviorSubject.next(null);
      }

      // New Year = New Session
      let yearChanged = true;
      if (drxState.effectiveDateStored) {
        const effectiveDateState: EffectiveDateStateStructure = this.reducerUtilityService.getReducerFromApplicationState('EffectiveDate');
        yearChanged = drxState.effectiveDateStored !== effectiveDateState.effectiveDate;
      }

      // New Region = New Session
      let regionChanged = false;
      if (drxState.countyStored) {
        const zipCountyState: ZipCountyStateStructure = this.reducerUtilityService.getState('ZipCounty');
        regionChanged = drxState.countyStored.region !== zipCountyState.county?.region;
      }

      /** New Meds === New Session */
      const startNewSession = this.hasNewMedsOrPharmacies(drxState.drxPreviousEncodedSessionMeds) || 
          (medState.currentEncodedMeds !== null && isSessionUnstartedOrExpired(drxState.drxSessionStartTimeInMS)) || 
          ((yearChanged || regionChanged) && medState.currentEncodedMeds !== null);

      /** Generate new Session, record reference in state to active meds */
      if (startNewSession) {
        // Clear data so we know to show loading spinners for drug data
        this.drxSessionDataBehaviorSubject.next(null);

        /** Generate time now before the async call, just for the sake of ensuring safety */
        const drxSessionStartTimeInMS: number = getTimeNowInMS();

        await this.startSessionAndRetrievePlans();

        this.ngRedux.dispatch(
          setPreviousEncodedSessionMeds(
            this.getCurrentEncodedMedsAndPharmacies(),
            drxSessionStartTimeInMS
          )
        );
      }

      this.sessionStarting = false;
    }
  }

  public async updatePlanDetails() {
    const drxState: DRXStateStructure = this.reducerUtilityService.getState('DrxServiceData');
    const medState: MedicationStateStructure = this.reducerUtilityService.getState('MedicationInformation');
    const planState: PlansStateStructure = this.reducerUtilityService.getState('Plans');

    /** Current Plan's State is Drx's Plan state === no plan change */
    const isNewPlanSelected: boolean = (
      planState.medicareAdvantage_or_PDP_planForDRX &&
      planState.medicareAdvantage_or_PDP_planForDRX.attributes &&
        (planState.medicareAdvantage_or_PDP_planForDRX.attributes.PBP_ID !== drxState.drxSelectedPlanContractId ||
          planState.medicareAdvantage_or_PDP_planForDRX.year != drxState.drxSingleProductResponse.PlanYear)
    );

    /** Get new estimates if we have a selected plan and the plan is either new or the meds are new */
    if (medState.currentEncodedMeds !== null &&
        (isNewPlanSelected || (planState.medicareAdvantage_or_PDP_planForDRX && this.hasNewMedsOrPharmacies(drxState.drxPreviousEncodedDetailMeds)))) {

      await this.newProduct(planState.medicareAdvantage_or_PDP_planForDRX);

      this.ngRedux.dispatch(
        setSelectedPlanContractId(planState.medicareAdvantage_or_PDP_planForDRX.attributes.PBP_ID)
      );

      this.ngRedux.dispatch(
        setPreviousEncodedDetailMeds(this.getCurrentEncodedMedsAndPharmacies())
      );

      this.generateAndSaveRetailAndMailData();
    }
  }

  /** Uses the MedicationInformation state to start a new DRX session - xhr */
  private async startSessionAndRetrievePlans(): Promise<void> {

    const sessionStartResponseArray: DRXSessionStartResponse[] = await this.drxSessionStart();


    const sessionId: string = sessionStartResponseArray[0].SessionID;
    this.ngRedux.dispatch(setSessionId(sessionId));

    const retrievedDrxPlans: DRXRetrieveAllProductsResponse = await this.drxPlansBySessionId(sessionId);

    const effectiveDateState: EffectiveDateStateStructure = this.reducerUtilityService.getReducerFromApplicationState('EffectiveDate');
    const zipCountyState: ZipCountyStateStructure = this.reducerUtilityService.getState('ZipCounty');
    this.ngRedux.dispatch(setEffectiveDateStored(effectiveDateState.effectiveDate));
    this.ngRedux.dispatch(setCountyStored(zipCountyState.county));

    if (retrievedDrxPlans && retrievedDrxPlans.MedicarePlans) {
      this.ngRedux.dispatch(setDrxAllProductsResponse(retrievedDrxPlans.MedicarePlans));
      this.drxSessionDataBehaviorSubject.next(retrievedDrxPlans.MedicarePlans);
    } else {
      console.error('No matching plans retrieved from DRX service');
      this.ngRedux.dispatch(setDrxAllProductsResponse(null));
      this.drxSessionDataBehaviorSubject.next([]);
    }
  }

  /**  Uses passed plan to generate DRX plan data */
  private async newProduct(plan: MedicareAdvantage_and_PDP_Product): Promise<DRXRetrieveSingleProductResponse> {
    const drxState: DRXStateStructure = this.reducerUtilityService.getState('DrxServiceData');
    const retrievedDrxPlan: DRXRetrieveSingleProductResponse = await this.drxPlanBySessionIdAndPlanId(drxState.drxSessionId, plan);
    this.ngRedux.dispatch(setDrxSingleProductResponse(retrievedDrxPlan));
    return retrievedDrxPlan;
  }

  public async loadDrugCoverageDetails(plan: MedicareAdvantage_and_PDP_Product): Promise<DRXRetrieveSingleProductResponse> {
    if (!this.drugCache[plan.attributes.PLAN_ID]) {
      const drugResponse: DRXRetrieveSingleProductResponse = await this.newProduct(plan);
      this.drugCache[plan.attributes.PLAN_ID] = drugResponse;
    }

    return this.drugCache[plan.attributes.PLAN_ID];
  }

  /** DRX Session Methods */
  async drxSessionStart(): Promise<DRXSessionStartResponse[]> {

    const pharmacyState: PharmacyState = this.reducerUtilityService.getState(PHARMACY_REDUX_STATE_ID);
    const medicationInformation: MedicationStateStructure = this.reducerUtilityService.getState('MedicationInformation');

    const drxDosages: DRXDosagePayload[] = [];
    const pharmacyData: DRXPharmacyPayload[] = [];

    // If the user leaves the medication page after adding a drug, 
    // but doesn't fill out the data we have to remove it
    this.removeIncompleteMeds(medicationInformation);

    /** Gets dosages from Selected Medications and formats a partial payload */
    medicationInformation.medicationForm.selectedMedications.forEach(
      (selectedMed: SelectedMedication) => {
        // All meds should have data at this point after removing the incomplete ones earlier
        const selectedDosageData: DrugDosage = this.getDosageDataForMed(selectedMed)[0];

        drxDosages.push({
          DaysOfSupply: selectedDosageData.CommonDaysOfSupply.toString(),
          MetricQuantity:
            selectedDosageData.CommonUserQuantity > 1
              ? selectedMed.dosageQuantity.toString()
              : (
                Number(selectedMed.dosageQuantity) *
                Number(selectedDosageData.CommonMetricQuantity)
              ).toString(),
          NDC: selectedDosageData.ReferenceNDC
        });
      }
    );

    /** Gets pharmacies from Selected Pharmacies and formats a partial payload */
    pharmacyState.pharmacys.forEach(
      (pharm: Pharmacy) => {
        pharmacyData.push({
          PharmacyID: pharm.pharmacyId,
          IsMailOrder: false,
          PharmacyIDType: 0
        });
      }
    );

    // console.log(
    //   '%cBirthdate for DRX Session is hard-coded for medicare advantage, needs to be set appropriately for medigap',
    //   'font-weight: bold'
    // );

    const drxSessionStartPayload: DRXDatapowerSessionStartPayload = {
      Session: {
        Birthdate: null,
        HealthStatus: '3',
        Dosages: {
          Dosage: drxDosages
        },
        Pharmacies: {
          Pharmacy: pharmacyData
        }
      }
    };

    let strategy = environment.infrastructure.connecture.httpStrategy || XHR_STRATEGIES.AXWAY_PRE_PASS;
    let payload: DRXAxwaySessionStartPayload[] | DRXDatapowerSessionStartPayload = drxSessionStartPayload;

    // Datapower is processing the request payload and converting it into XML. It requires an old formatting to work.
    // We are moving to axway, which does not do this.  This means there is a difference in request objects for each service
    // We can check for the Axway configuration here. If it is using the new Axway URLs, we have to reconstruct the payload manually
    if (strategy === XHR_STRATEGIES.AXWAY_PRE_PASS) {
      let axwayPayload: DRXAxwaySessionStartPayload = {
        Birthdate: drxSessionStartPayload.Session.Birthdate,
        HealthStatus: drxSessionStartPayload.Session.HealthStatus,
        Dosages: drxSessionStartPayload.Session.Dosages.Dosage,
        Pharmacies: drxSessionStartPayload.Session.Pharmacies.Pharmacy
      }
      payload = [axwayPayload]; //Axway also requires wrapping the whole object in an array
    }
    else {
      payload = drxSessionStartPayload;
    }

    const requestConfigImplementation = new RequestConfigImplementation({
      method: REQUEST_METHODS.POST,
      url: environment.infrastructure.connecture.sessionUrlPath,
      POST_OR_PUT_PAYLOAD: payload,
      headers: new HttpHeaders({
        ClientID: 'Highmark'
      })
    });

    this.httpClient.setStrategy(strategy);
    return this.httpClient.makeRequest<DRXSessionStartResponse[]>(requestConfigImplementation);
  }

  // Uses the same criteria for pulling 
  private removeIncompleteMeds(medicationInformation: MedicationStateStructure) {
    medicationInformation.medicationForm.selectedMedications = medicationInformation.medicationForm.selectedMedications.filter((selectedMed) => {
      return this.getDosageDataForMed(selectedMed).length;
    });
  }

  private getDosageDataForMed(selectedMed: SelectedMedication): DrugDosage[] {
    return selectedMed.drug.Dosages.filter((dose: DrugDosage) => dose.DosageID === selectedMed.selectedDosage);
  }

  createMemberSession(): Promise<DRXSessionStartResponse[]> {
    const requestConfigImplementation = new RequestConfigImplementation({
      method: REQUEST_METHODS.POST,
      url: '/apiplancompare/v3/Session',
      POST_OR_PUT_PAYLOAD: [
        {
            BLUserName: 'salesPortalMemberSearch'
        }
      ]
    });

    this.httpClient.setStrategy(XHR_STRATEGIES.AXWAY_POST_PASS);
    return this.httpClient.makeRequest<DRXSessionStartResponse[]>(requestConfigImplementation);
  }

  searchMember(sessionId: string, payload: DRXMemberSearchPayload): Promise<DRXMemberSearchResponse[]> {
    const requestConfigImplementation = new RequestConfigImplementation({
      method: REQUEST_METHODS.GET,
      url: `/apiplancompare/v3/Session/${sessionId}/MemberSearch/GetMemberEnrollments`,
      GET_PAYLOAD: payload
    });

    this.httpClient.setStrategy(XHR_STRATEGIES.AXWAY_POST_PASS);
    return this.httpClient.makeRequest<DRXMemberSearchResponse[]>(requestConfigImplementation);
  }

  /*

  TODOOOOO
  ADD CLIENT ID HEEADER 

						{
							"key": "dateOfBirth",
							"value": "09/09/1951"
						},
						{
							"key": "firstName",
							"value": "Brandon",
							"disabled": true
						},
						{
							"key": "lastName",
							"value": "test",
							"disabled": true
						}

  */

  createSessionFromSsoValue(ssoValue: string): Promise<DRXSessionStartResponse[]> {
    const requestConfigImplementation = new RequestConfigImplementation({
      method: REQUEST_METHODS.POST,
      url: '/apiplancompare/v3/Session',
      POST_OR_PUT_PAYLOAD: [
        {
            SSOValue: ssoValue
        }
      ]
    });

    this.httpClient.setStrategy(XHR_STRATEGIES.AXWAY_POST_PASS);
    return this.httpClient.makeRequest<DRXSessionStartResponse[]>(requestConfigImplementation);
  }

  getMemberInformationForSession(sessionId: string): Promise<DRXSessionStartResponse> {
    const requestConfigImplementation = new RequestConfigImplementation({
      method: REQUEST_METHODS.GET,
      url: `/apiplancompare/v3/Session/${sessionId}`,
      headers: new HttpHeaders({
        ClientID: 'Highmark'
      })
    });

    this.httpClient.setStrategy(XHR_STRATEGIES.AXWAY_POST_PASS);
    return this.httpClient.makeRequest<DRXSessionStartResponse>(requestConfigImplementation);
  }

  async drxPlansBySessionId(sessionId: string): Promise<DRXRetrieveAllProductsResponse> {

    const zipCountyState: ZipCountyStateStructure = this.reducerUtilityService.getState('ZipCounty');
    const effectiveDateState: EffectiveDateStateStructure = this.reducerUtilityService.getReducerFromApplicationState('EffectiveDate');

    if (zipCountyState && zipCountyState.zipCode && zipCountyState.county && zipCountyState.county.fipsCode) {
      const requestConfigImplementation = new RequestConfigImplementation({
        method: REQUEST_METHODS.GET,
        url: `${environment.infrastructure.connecture.sessionUrlPath}/${sessionId}/Plans`,
        GET_PAYLOAD: {
          EffectiveDate: effectiveDateState.effectiveDate,
          FIPS: zipCountyState.county.fipsCode, //  '42125',
          Year: effectiveDateState.effectiveDateYear,
          Zip: zipCountyState.zipCode //  '15323'
        }
      });

      let strategy = environment.infrastructure.connecture.httpStrategy || XHR_STRATEGIES.AXWAY_PRE_PASS;
      this.httpClient.setStrategy(strategy);
      return this.httpClient.makeRequest<DRXRetrieveAllProductsResponse>(requestConfigImplementation);
    }
  }

  async drxPlanBySessionIdAndPlanId(sessionId: string, plan: MedicareAdvantage_and_PDP_Product): Promise<DRXRetrieveSingleProductResponse> {

    const getDRXPlanId = (selectedPlan: MedicareAdvantage_and_PDP_PlanAttributes) => {
      const drxState: DRXStateStructure = this.reducerUtilityService.getState(
        'DrxServiceData'
      );

      if (drxState.drxAllProductsResponse) {
        const filteredDrxProducts = drxState.drxAllProductsResponse.filter(product => {
          return product?.PlanID === selectedPlan?.PBP_ID && product?.ContractID === selectedPlan?.CMS_PLAN_ID;
        });

        if (filteredDrxProducts.length > 0) {
          return filteredDrxProducts[0].ID;
        }
      }

    };

    const zipCountyState: ZipCountyStateStructure = this.reducerUtilityService.getState('ZipCounty');
    const effectiveDateState: EffectiveDateStateStructure = this.reducerUtilityService.getReducerFromApplicationState('EffectiveDate');

    let queryParams = {
      EffectiveDate: effectiveDateState.effectiveDate,
      FIPS: zipCountyState.county.fipsCode, // '42125',
      Year: effectiveDateState.effectiveDateYear,
      Zip: zipCountyState.zipCode, // '15323'
      ShowDefault: true, // if true, will only return default if no pharmacies are selected
      showPharmacy: true, //  if true, will return session pharamacies
      showDefaultPharmacyPricing: false //  if true, will always return default pharamcy, regardless of session pharmacies
    };

    if (zipCountyState && zipCountyState.zipCode && zipCountyState.county && zipCountyState.county.fipsCode) {

      const drxPlanId = getDRXPlanId(plan.attributes);

      if (drxPlanId) {
        const requestConfigImplementation = new RequestConfigImplementation({
          method: REQUEST_METHODS.GET,
          url: `${environment.infrastructure.connecture.sessionUrlPath}/${sessionId}/Plans/${drxPlanId}`,
          GET_PAYLOAD: queryParams,
          headers: new HttpHeaders({
            ClientID: 'Highmark'
          })
        });

        let strategy = environment.infrastructure.connecture.httpStrategy || XHR_STRATEGIES.AXWAY_PRE_PASS;
        this.httpClient.setStrategy(strategy);
        return this.httpClient.makeRequest<DRXRetrieveSingleProductResponse>(requestConfigImplementation);
      } else {
        console.error('No DRX Plan ID was found.');
        return null;
      }

    } else {
      return null;
    }
  }

  private getPharmacyCostObjectByType(data, pharmacyType: number): PharmacyCost {
    const pharmacyState: PharmacyState = this.reducerUtilityService.getState(PHARMACY_REDUX_STATE_ID);
    let pharmacyCosts: PharmacyCost;

    // Selected pharmacies are always retail
    if (pharmacyState.selectedPharmacy && (pharmacyType === RETAIL_PHARMACY_TYPE_ID)) {
      pharmacyCosts = data.PharmacyCosts.filter((pharm: PharmacyCost) => pharm.PharmacyID === pharmacyState.selectedPharmacy.pharmacyId)[0];
    }
    
    // Will fallback to this if a pharmacy isn't selected or is a mail order type
    if (!pharmacyCosts) {
      pharmacyCosts = data.PharmacyCosts.filter((pharm) => pharm.PharmacyType == pharmacyType)[0];
    }

    return pharmacyCosts;
  }

  /** Transformation Methods */
  private getDrugCostsByMonth(data: DRXRetrieveSingleProductResponse, pharmacyType) {
    const toReturn = {
      hasTopper: false,
      headerRow: ['Drug Name', 'Phase', 'Amount'],
      tableBody: [
        /* {head:'January', row:['Atorvastatin Calcium 10 MG', 'Deductible', '$50.00']}, ...*/
      ]
    };

    const pharmacyCosts = this.getPharmacyCostObjectByType(data, pharmacyType);

    pharmacyCosts.MonthlyCosts.forEach(monthObj => {
      const monthLabel = this.monthsArray[monthObj.MonthID];
      let drugName = '';
      let phase = '';
      let amount = '';
      monthObj.CostDetail.forEach(costObj => {
        drugName += costObj.LabelName + '<br>';
        phase += this.getPhase(data, costObj) + '<br/>';
        amount += (costObj.MemberCost || costObj.MemberCost === 0) ? this.getFormattedCost(costObj.MemberCost) : '';
        amount += '<br/>';
      });

      const row = [drugName, phase, amount];
      toReturn.tableBody.push({
        head: monthLabel,
        row
      });
    });

    return toReturn;
  }

  private getPhase(data: DRXRetrieveSingleProductResponse, costObj: DRXCostObj) {
    let phase = '';
    data.PlanDrugCoverage.forEach(pdc => {
      if (pdc.LabelName === costObj.LabelName) {
        phase = pdc.TierNumber === 0 ? 'Not Covered' : costObj.Phase;
      }
    });

    return phase || costObj.Phase;
  }

  private getMonthlyCosts(data, pharmacyType: number) {


    const toReturn = {
      hasTopper: false,
      headerRow: this.monthlyCostsHeaderContent,
      tableBody: []
    };

    const monthsArray = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
      'Annual Total'
    ];

    const pharmacyCosts = this.getPharmacyCostObjectByType(data, pharmacyType);
    const drugCosts = pharmacyCosts.DrugCosts;

    let totalDrugsCosts = 0;
    let totalPremium = 0;
    let totalMonthlyCosts = 0;

    pharmacyCosts.MonthlyCosts.forEach(monthObj => {
      const monthLabel = this.monthsArray[monthObj.MonthID];
      let phase = '';
      let amount = 0;
      const premium = data.MedicalPremium + data.DrugPremium;

      monthObj.CostDetail.forEach(costObj => {
        if (!phase) {
          phase = costObj.Phase;
        }
        amount += (costObj.MemberCost || costObj.MemberCost === 0) ? costObj.MemberCost : 0;
      });

      const monthlyCosts = premium + amount;
      totalDrugsCosts += amount;
      totalPremium += premium;
      totalMonthlyCosts += monthlyCosts;

      const row = [
        this.currencyPipe.transform(amount, 'USD', 'symbol', '1.2-2'),
        phase,
        this.currencyPipe.transform(premium, 'USD', 'symbol', '1.2-2'),
        this.currencyPipe.transform(monthlyCosts, 'USD', 'symbol', '1.2-2')
      ];

      toReturn.tableBody.push({
        head: monthLabel,
        row
      });
    });

    const bottomRow = [
      this.currencyPipe.transform(totalDrugsCosts, 'USD', 'symbol', '1.2-2'),
      '&#8212;',
      this.currencyPipe.transform(totalPremium, 'USD', 'symbol', '1.2-2'),
      this.currencyPipe.transform(totalMonthlyCosts, 'USD', 'symbol', '1.2-2')
    ];

    toReturn.tableBody.push({
      head: 'Total Yearly Costs',
      row: bottomRow
    });

    return toReturn;
  }

  private getMonthlyCostsAfterDeductible(data: DRXRetrieveSingleProductResponse, pharmacyType) {
    let shouldIncludeDeductible = this.shouldIncludeDeductible();
    let shouldIncludeCoverageGap = this.shouldIncludeCoverageGap(data);
    let headerRow = [].concat(this.headerRowContent);

    // Insert coverage gap column directly after initial coverage
    if (shouldIncludeCoverageGap) {
      headerRow.splice(headerRow.indexOf(this.INITIAL_COVERAGE_HEADER)+ 1, 0, this.COVERAGE_GAP_HEADER);
    }
    // Insert deductible column directly after retail costs
    if (shouldIncludeDeductible) {
      headerRow.splice(headerRow.indexOf(this.RETAIL_COST_HEADER) + 1, 0, this.DEDUCTIBLE_HEADER);
    }

    const toReturn = {
      hasTopper: true,
      tableTop: this.tableCategoriesContent,
      headerRow: headerRow,
      tableBody: [
        // {head:'Atorvastatin Calcuim TAB 10 MG', row:['$20.00','$25.00','$40.00','$100.00', 'No', '45/30 days', 'No']}, ...
      ]
    };

    const planDrugCoverages = data.PlanDrugCoverage;
    const pharmacyCosts = this.getPharmacyCostObjectByType(data, pharmacyType);

    planDrugCoverages.forEach(drug => {
      drug.tierName = drug.TierDescription ? drug.TierDescription : '';
      drug.quantityLimit = drug.HasQuantityLimit ?
        drug.QuantityLimitDescription + ' per ' + drug.QuantityLimitDays + ' days' : '';

      const selectedMedications: SelectedMedication[] = this.reducerUtilityService.getState<
        MedicationStateStructure
      >('MedicationInformation').medicationForm.selectedMedications;

      /****************** Need to Pass pharmacy costs to get data to complete this! *******************/
      pharmacyCosts.DrugCosts.forEach((costObj) => {
        if (costObj.LabelName === drug.LabelName) {
          const head = drug.LabelName;
          let quantity = '';

          selectedMedications.forEach((med: SelectedMedication) => {
            const drugInfo: DrugItem = med.drug;

            if (med.drug.DrugName === drug.LabelName) {
              quantity =
                this.drugLookupService.getCalculatedDosageQuantityPerMonth(
                  drugInfo.Dosages,
                  med.selectedDosage,
                  med.selectedPackage,
                  med.dosageQuantity,
                  med.frequency
                ) + ' per month';
            }
          });

          const descript = drug.TierNumber === 0 ? 'Not Covered' : 'Tier ' + drug.TierNumber;
          
          const retailCost = this.getFormattedCost(costObj.FullCost);
          const deductible = this.getFormattedCost(costObj.Deductible);
          const beforeGap = this.getFormattedCost(costObj.BeforeGap);
          const coverageGap = this.getFormattedCost(costObj.Gap);
          const afterGap = this.getFormattedCost(costObj.AfterGap);

          const priorAuth = drug.HasPriorAuthorization ? 'Yes' : ' ';
          const quantityLimits = drug.quantityLimit ? drug.quantityLimit : ' ';
          const stepTherapy = drug.HasStepTherapy ? 'Yes' : ' ';

          const row = [
            retailCost,
            beforeGap,
            afterGap,
            priorAuth,
            quantityLimits,
            stepTherapy
          ];

          // Insert the actual data if these columns are being shown
          if (shouldIncludeDeductible) {
            row.splice(headerRow.indexOf(this.DEDUCTIBLE_HEADER), 0, deductible);
          }
          if (shouldIncludeCoverageGap) {
            row.splice(headerRow.indexOf(this.COVERAGE_GAP_HEADER), 0, coverageGap);
          }

          toReturn.tableBody.push({
            head,
            descript,
            row
          });
        }
      });
    });
  
    return toReturn;
  }

  private shouldIncludeDeductible() {
    // Only New York and HM currently has the deductible column - easier to check the brands that don't apply
    let nonDeductibleRegions = [brandMN, brandWY, brandND];
    const zipCounty: ZipCountyStateStructure = this.reducerUtilityService.getState('ZipCounty');
    let brandFound = nonDeductibleRegions.filter((brand) => brand.region === zipCounty.county.region);
    if (brandFound.length === 0) {
      return true;
    }

    return false;
  }

  private shouldIncludeCoverageGap(data: DRXRetrieveSingleProductResponse): boolean {
    return data.PlanYear && +data.PlanYear <= 2024;
  }

  private getFormattedCost(cost) {
    return cost ? this.currencyPipe.transform(cost, 'USD', 'symbol', '1.2-2') : '$0';
  }

  /** Generate Map */
  generateAndSaveRetailAndMailData() {
    const singleProductFromDRX = this.reducerUtilityService.getState<DRXStateStructure>('DrxServiceData').drxSingleProductResponse;
    let retail: DRXMonthlyCosts = null;
    let mailOrder: DRXMonthlyCosts = null;
    let retailTableData: DRXGraphAndTableDataChild = null;
    let mailOrderTableData: DRXGraphAndTableDataChild = null;

    // Check for retail pharmacy costs
    if (singleProductFromDRX.PharmacyCosts?.filter((pharm) => pharm.PharmacyType == RETAIL_PHARMACY_TYPE_ID).length) {
      retail = {
        drug: this.getDrugCostsByMonth(singleProductFromDRX, RETAIL_PHARMACY_TYPE_ID),
        monthly: this.getMonthlyCosts(singleProductFromDRX, RETAIL_PHARMACY_TYPE_ID),
        monthlyAfterDeductible: this.getMonthlyCostsAfterDeductible(singleProductFromDRX, RETAIL_PHARMACY_TYPE_ID)
      };
      retailTableData = this.generateRetailTableData(retail);
    }

    // Check for mail order pharmacy costs
    if (singleProductFromDRX.PharmacyCosts?.filter((pharm) => pharm.PharmacyType == MAIL_PHARMACY_TYPE_ID).length) {
      mailOrder = {
        drug: this.getDrugCostsByMonth(singleProductFromDRX, MAIL_PHARMACY_TYPE_ID),
        monthly: this.getMonthlyCosts(singleProductFromDRX, MAIL_PHARMACY_TYPE_ID),
        monthlyAfterDeductible: this.getMonthlyCostsAfterDeductible(singleProductFromDRX, MAIL_PHARMACY_TYPE_ID)
      };
      mailOrderTableData = this.generateMailTableData(mailOrder);
    }

    const graphData: DRXGraphAndTableData = {
      retail: retailTableData,
      mailOrder: mailOrderTableData
    };

    this.ngRedux.dispatch(setCostsData(graphData, mailOrder, retail));
  }

  private generateRetailTableData(retail: DRXMonthlyCosts): DRXGraphAndTableDataChild {
    const retailDonutAnnual = [];
    const retailDonutMonthly = {};

    let retailTotalPremium = 0;
    const retailPhaseCosts = {};

    retail.monthly.tableBody.forEach((month, index) => {
      const totalCost = Number(month.row[3].replace(/[$,]*/g, ''));
      const drugPhase = month.row[1];
      const phaseCost = Number(month.row[0].replace(/[$,]*/g, ''));
      const premiumCost = Number(month.row[2].replace(/[$,]*/g, ''));
      const months = [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December'
      ];


      if (index < 12) {
        retailDonutMonthly[months[index]] = {
          total: totalCost,
          breakdown: [[drugPhase, phaseCost], ['PREMIUM', premiumCost]]
        };

        retailTotalPremium += premiumCost;
        retailPhaseCosts[drugPhase] =
          phaseCost + (retailPhaseCosts[drugPhase] || 0);
      } else {
        const phases = Object.keys(retailPhaseCosts);
        for (let i = 0; i < phases.length; i++) {
          retailDonutAnnual.push([phases[i], retailPhaseCosts[phases[i]]]);
        }
        retailDonutAnnual.push(['PREMIUM', retailTotalPremium]);
      }
    });

    return {
      barTotals: retail.monthly.tableBody.map(month => {
        return Number(month.row[3].replace(/[$,]*/g, ''));
      }),
      donutAnnual: retailDonutAnnual,
      donutMonthly: retailDonutMonthly
    }
  }

  private generateMailTableData(mailOrder: DRXMonthlyCosts): DRXGraphAndTableDataChild {
    const mailOrderDonutAnnual = [];
    const mailOrderDonutMonthly = {};
    let mailOrderTotalPremium = 0;
    const mailOrderPhaseCosts = {};

    // separate MailOrder and Retail
    mailOrder.monthly.tableBody.forEach((month, index) => {
      const totalCost = Number(month.row[3].replace(/[$,]*/g, ''));
      const drugPhase = month.row[1];
      const phaseCost = Number(month.row[0].replace(/[$,]*/g, ''));
      const premiumCost = Number(month.row[2].replace(/[$,]*/g, ''));
      const months = [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December'
      ];

      if (index < 12) {
        mailOrderDonutMonthly[months[index]] = {
          total: totalCost,
          breakdown: [[drugPhase, phaseCost], ['PREMIUM', premiumCost]]
        };
        mailOrderTotalPremium += premiumCost;
        mailOrderPhaseCosts[drugPhase] =
          phaseCost + (mailOrderPhaseCosts[drugPhase] || 0);
      } else {
        const phases = Object.keys(mailOrderPhaseCosts);
        for (let i = 0; i < phases.length; i++) {
          mailOrderDonutAnnual.push([
            phases[i],
            mailOrderPhaseCosts[phases[i]]
          ]);
        }
        mailOrderDonutAnnual.push(['PREMIUM', mailOrderTotalPremium]);
      }
    });

    return {
      barTotals: mailOrder.monthly.tableBody.map(month => {
        return Number(month.row[3].replace(/[$,]*/g, ''));
      }),
      donutAnnual: mailOrderDonutAnnual,
      donutMonthly: mailOrderDonutMonthly
    }
  }
}

